import React, { useEffect, useState } from "react";
import { AbsoluteFill, continueRender, delayRender, interpolate, useCurrentFrame, useVideoConfig } from "remotion";

import { ensureCaptionFont } from "./fonts/montserrat";

/**
 * Warstwa napisów w identyfikacji marki (TRE-1979, etap B „spice"). Dostaje strony policzone
 * przez clip-plan.logic na WYJŚCIOWEJ osi czasu klipu. Ten sam komponent maluje napisy w
 * renderze serwerowym (CaptionedClip) i w podglądzie edytora (ClipPreview), więc oba nie mogą
 * się rozjechać.
 *
 * Styl z playbooka: krótkie strony (2–3 słowa) w środku kadru przy kadrze na twarz, wagi jako
 * „gałka emfazy" (Medium → Bold → Black), miękki dwuwarstwowy cień zamiast twardego obrysu,
 * wjazd strony z góry o kilka pikseli, aktualnie wypowiadane słowo podświetlone.
 */

export type CaptionEmphasisLevel = "base" | "strong" | "payoff";

export interface RemotionCaptionWord {
  text: string;
  fromMs: number;
  toMs: number;
  emphasis?: CaptionEmphasisLevel;
}

export interface RemotionCaptionEvent {
  fromMs: number;
  toMs: number;
  lines: RemotionCaptionWord[][];
}

export interface CaptionedClipStyle {
  fontFamily: string;
  fontSize: number;
  highlightColor: string;
  baseColor: string;
  outlineColor: string;
  /** Odległość napisów od dołu kadru (anchor „bottom") — ponad overlayem UI Reels/TikToka. */
  marginBottom: number;
  /** bottom: pełny obraz z pasami (napisy pod obrazem); center: kadr na twarz (napisy na piersi). */
  anchor?: "bottom" | "center";
  /** Wagi fontu zmiennego dla poziomów emfazy. */
  weightBase?: number;
  weightStrong?: number;
  weightPayoff?: number;
  /** Maksymalna szerokość wiersza jako ułamek kadru — safe zone UI. */
  maxWidthRatio?: number;
  /** soft: dwuwarstwowy cień (playbook); outline: twardy obrys (stary wygląd). */
  shadow?: "soft" | "outline";
  /** Wjazd strony z góry (px) i czas (ms); 0 = bez animacji. */
  dropInPx?: number;
  dropInMs?: number;
}

const outlineShadow = (color: string) =>
  `-3px -3px 0 ${color}, 3px -3px 0 ${color}, -3px 3px 0 ${color}, 3px 3px 0 ${color}, 0 6px 12px rgba(0,0,0,0.45)`;

/** Dwie warstwy: rozległa poświata + kierunkowy cień w dół-prawo, tak jak preset z Premiere w kicie. */
const softShadow = (color: string) => `0 0 18px ${color}cc, 0 0 6px ${color}, 3px 5px 8px ${color}e6`;

const useCaptionFont = (fontFamily: string) => {
  const [handle] = useState(() => delayRender(`font ${fontFamily}`));
  useEffect(() => {
    let active = true;
    ensureCaptionFont()
      .catch(() => undefined)
      .then(() => {
        if (active) continueRender(handle);
      });
    return () => {
      active = false;
      continueRender(handle);
    };
  }, [handle]);
};

export const CaptionOverlay: React.FC<{
  currentMs: number;
  events: RemotionCaptionEvent[];
  style: CaptionedClipStyle;
}> = ({ currentMs, events, style }) => {
  useCaptionFont(style.fontFamily);
  const frame = useCurrentFrame();
  const { fps, width } = useVideoConfig();
  const activeEvent = events.find((event) => currentMs >= event.fromMs && currentMs < event.toMs);
  if (!activeEvent) return null;

  const anchor = style.anchor ?? "bottom";
  const weights = {
    base: style.weightBase ?? 500,
    strong: style.weightStrong ?? 700,
    payoff: style.weightPayoff ?? 900,
  };
  const dropInPx = style.dropInPx ?? 8;
  const dropInMs = style.dropInMs ?? 500;
  const sinceStartMs = currentMs - activeEvent.fromMs;
  const progress = dropInMs > 0 ? Math.min(1, Math.max(0, sinceStartMs / dropInMs)) : 1;
  const eased = 1 - (1 - progress) * (1 - progress);
  const translateY = -dropInPx * (1 - eased);
  const opacity = interpolate(Math.min(sinceStartMs, 83), [0, 83], [0.9, 1]);
  const maxWidth = Math.round(width * (style.maxWidthRatio ?? 0.82));
  // Long lines shrink instead of leaving the safe zone (the kit's width cap).
  const longestLine = Math.max(...activeEvent.lines.map((line) => line.map((word) => word.text).join(" ").length), 1);
  const estimatedWidth = longestLine * style.fontSize * 0.58;
  const fontSize =
    estimatedWidth > maxWidth ? Math.floor((style.fontSize * maxWidth) / estimatedWidth) : style.fontSize;
  void frame;
  void fps;

  return (
    <AbsoluteFill
      style={{
        justifyContent: anchor === "center" ? "center" : "flex-end",
        alignItems: "center",
        paddingBottom: anchor === "center" ? 0 : style.marginBottom,
        paddingLeft: 60,
        paddingRight: 60,
      }}
    >
      <div
        style={{
          fontFamily: style.fontFamily,
          fontSize,
          fontWeight: weights.base,
          lineHeight: 1.18,
          textAlign: "center",
          maxWidth,
          textShadow:
            (style.shadow ?? "soft") === "soft" ? softShadow(style.outlineColor) : outlineShadow(style.outlineColor),
          transform: `translateY(${translateY}px)`,
          opacity,
        }}
      >
        {activeEvent.lines.map((line, lineIndex) => (
          <div key={lineIndex}>
            {line.map((word, wordIndex) => (
              <span
                key={wordIndex}
                style={{
                  color: currentMs >= word.fromMs ? style.highlightColor : style.baseColor,
                  fontWeight: weights[word.emphasis ?? "base"],
                  fontSize: word.emphasis === "payoff" ? "1.06em" : undefined,
                  // Skok koloru bez skalowania słowa: skalowanie rozjeżdża łamanie
                  // wersów policzone po polsku w clip-plan.logic.
                  transition: "none",
                }}
              >
                {word.text}
                {wordIndex < line.length - 1 ? " " : ""}
              </span>
            ))}
          </div>
        ))}
      </div>
    </AbsoluteFill>
  );
};
